import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'LocalBusiness' | 'FAQPage';
  data: Record<string, any>;
}

export function useSchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const scriptId = `schema-org-${type.toLowerCase()}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    let schemaData: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': type,
      ...data,
    };
    
    // Replace placeholder URLs with actual site URL
    const jsonString = JSON.stringify(schemaData);
    const updatedJsonString = jsonString.replace(/https:\/\/example\.com/g, siteUrl);
    schemaData = JSON.parse(updatedJsonString);
    
    script.textContent = JSON.stringify(schemaData);
    
    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);
}

export function OrganizationSchema() {
  useSchemaOrg({
    type: 'Organization',
    data: {
      name: 'IDKnit',
      url: 'https://example.com',
      logo: 'https://example.com/logo.png',
      description: 'インダストリアルニット：塗装レスで機能を編み込み、カスタムデザインや用途に応じて柔軟に形を変える、これまでの表現では到達できなかった多様性を実現するソリューション',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '日坂1161-1',
        addressLocality: '鎌倉市',
        addressRegion: '神奈川県',
        postalCode: '247-0062',
        addressCountry: 'JP',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+81-467-78-8891',
        contactType: 'customer service',
        areaServed: 'JP',
        availableLanguage: ['Japanese', 'English'],
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.3122,
        longitude: 139.5461,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
    },
  });
  
  return null;
}

export function WebSiteSchema() {
  useSchemaOrg({
    type: 'WebSite',
    data: {
      name: 'IDKnit',
      url: 'https://example.com',
      description: 'インダストリアルニット：塗装レスで機能を編み込み、カスタムデザインや用途に応じて柔軟に形を変える、これまでの表現では到達できなかった多様性を実現するソリューション',
      inLanguage: 'ja',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://example.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  });
  
  return null;
}

export function LocalBusinessSchema() {
  useSchemaOrg({
    type: 'LocalBusiness',
    data: {
      '@type': 'LocalBusiness',
      name: 'IDKnit',
      image: 'https://example.com/logo.png',
      '@id': 'https://example.com',
      url: 'https://example.com',
      telephone: '+81-467-78-8891',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '日坂1161-1',
        addressLocality: '鎌倉市',
        addressRegion: '神奈川県',
        postalCode: '247-0062',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.3122,
        longitude: 139.5461,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
      sameAs: [],
    },
  });
  
  return null;
}
